import { Card, Col, Row } from 'react-bootstrap';
import corner2 from '@/assets/img/icons/spot-illustrations/corner-2.png';

import { useGetAnalysisContentQuery } from '@/app/PageSpeed/api/analysisApi';
import { useParams } from 'react-router-dom';
import NewAnalysisForm from '../components/NewAnalysisForm';
import NewAnalysisFormSkeleton from '../components/NewAnalysisFormSkeleton';
import Background from '@/components/common/Background';
import Flex from '@/components/common/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { urlToArray } from '@/helpers/utils';
import ContentTabs from '../components/internal-seo/ContentTabs';
import OverviewCards from '../components/internal-seo/OverviewCards';
import NoAnalysisData from '../components/NoAnalysisData';
import AnalysisIncompleteState from '../components/AnalysisIncompleteState';
import { isAnalysisFinished } from '../utils/analysisStatus';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import OverviewCardsSkeleton from '../components/OverviewCardsSkeleton';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';

const InternalSeo = () => {
  const { slug } = useParams<{ slug?: string }>();
  const access = useToolAccess('analysis');
  const {
    data: analysisData,
    isFetching: isLoadingDetails,
    isError,
    error,
    refetch,
    isFetching
  } = useGetAnalysisContentQuery(slug || '', {
    skip: access.isLoading || !access.haveAccess || !slug
  });
  const {
    title,
    description,
    url,
    canonical,
    robots,
    langauge,
    publisher,
    keywords,
    content
  } = analysisData?.data?.content || {};

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار واکاوی" />;
  }

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (!isLoadingDetails && !analysisData?.data) return <NoAnalysisData />;
  if (!isLoadingDetails && !isAnalysisFinished(analysisData?.data?.status)) {
    return (
      <AnalysisIncompleteState
        status={analysisData?.data?.status}
        taskId={analysisData?.data?.task_id}
        onRefresh={() => refetch()}
      />
    );
  }

  const headerStats = analysisData?.data?.statistics?.header_stats || {};
  const imageStats = analysisData?.data?.statistics?.image_stats;
  const linkStats = analysisData?.data?.statistics?.link_stats;
  const pageUrl = typeof url === 'string' && url ? url : '';
  const robotsFileUrl = robots?.robotsFileUrl || '';
  const headerRows = analysisData?.data?.content?.headers || [];
  const headerCount = (tag: string) =>
    Math.max(
      Number(headerStats[tag]) || Number(headerStats[tag.toUpperCase()]) || 0,
      headerRows.filter(
        header =>
          String(header.tagName || '').toLowerCase() === tag.toLowerCase()
      ).length
    );
  const imageTotal =
    imageStats?.alt?.total || analysisData?.data?.content?.images?.length || 0;
  const linkTotal =
    linkStats?.protocol?.total ||
    analysisData?.data?.content?.links?.length ||
    0;
  const formatCount = (value: number) => value.toLocaleString('fa-IR');

  return (
    <>
      {!isLoadingDetails ? (
        <NewAnalysisForm initialSettings={analysisData?.data?.settings} />
      ) : (
        <NewAnalysisFormSkeleton />
      )}
      <div dir="rtl" id="">
        {!isLoadingDetails ? (
          <>
            <OverviewCards
              canonical={canonical}
              description={description}
              keywords={keywords}
              langauge={langauge}
              publisher={publisher}
              url={pageUrl}
              robotsFileUrl={robotsFileUrl}
              robotsTag={robots?.robotsTag}
              title={title}
              estimated_time={content?.estimated_time}
              word_count={content?.word_count}
              sentences_count={content?.sentences_count}
            />
            <Card className="h-100 my-3 overflow-hidden">
              <Background image={corner2} className="rounded-soft bg-card" />
              <Card.Body className="z-999">
                <Row className="position-relative g-4">
                  <Col
                    xs={12}
                    lg={6}
                    className="d-flex justify-content-end justify-content-md-start flex-wrap gap-3"
                  >
                    <div className="icon-item icon-item-md border rounded-3 shadow-none me-2">
                      <FontAwesomeIcon
                        size="lg"
                        icon="align-right"
                        className={`text-primary`}
                      />
                    </div>
                    <div className="d-flex ms-3 gap-3">
                      {['h6', 'h5', 'h4', 'h3', 'h2', 'h1'].map(key => (
                        <Flex
                          key={key}
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <h6 className="mb-0 fw-bold text-700">
                            {key.toUpperCase()}
                          </h6>
                          <p className="mb-0 fs-0 text-900 fw-medium">
                            {formatCount(headerCount(key))}
                          </p>
                        </Flex>
                      ))}
                    </div>

                    <div className="d-flex gap-4 ms-5 justify-content-start">
                      <Flex
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <h6 className="mb-0 fw-bold text-700 ">تصاویر</h6>
                        <p className={`mb-0 fs-0 text-900 fw-medium`}>
                          {formatCount(imageTotal)}
                        </p>
                      </Flex>
                      <Flex
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <h6 className="mb-0 fw-bold text-700 ">پیوند</h6>
                        <p className={`mb-0 fs-0 text-900 fw-medium`}>
                          {formatCount(linkTotal)}
                        </p>
                      </Flex>
                    </div>
                  </Col>
                  <Col
                    xs={12}
                    lg={6}
                    className="d-flex align-items-center justify-content-end"
                  >
                    <div className="d-flex fw-bold gap-3 ms-5 justify-content-start align-items-center">
                      {pageUrl ? (
                        <a
                          target="_blank"
                          rel="noreferrer noopener"
                          href={`${urlToArray(
                            new URL(pageUrl).origin + new URL(pageUrl).pathname
                          )
                            .slice(0, 2)
                            .join('')}/sitemap.xml`}
                        >
                          Sitemap.xml
                        </a>
                      ) : (
                        <span>Sitemap.xml</span>
                      )}{' '}
                      {robotsFileUrl ? (
                        <a
                          target="_blank"
                          rel="noreferrer noopener"
                          href={robotsFileUrl}
                        >
                          Robots.txt
                        </a>
                      ) : (
                        <span>Robots.txt</span>
                      )}{' '}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            <ContentTabs
              headerStats={headerStats}
              imageStats={imageStats}
              metaStats={analysisData?.data?.statistics?.meta_stats}
              linkStats={linkStats}
              densityStats={analysisData?.data?.statistics?.density_stats}
              contentData={analysisData?.data?.content}
            />
          </>
        ) : (
          <>
            <OverviewCardsSkeleton />
            <Card className="h-100 my-3 overflow-hidden">
              <Background image={corner2} className="rounded-soft bg-card" />
              <Card.Body className="z-999">
                <Row className="position-relative g-4">
                  <Col
                    xs={12}
                    lg={6}
                    className="d-flex justify-content-end justify-content-md-start flex-wrap gap-3"
                  >
                    <div className="icon-item icon-item-md border rounded-3 shadow-none me-2">
                      <FontAwesomeIcon
                        size="lg"
                        icon="align-right"
                        className={`text-primary`}
                      />
                    </div>
                    <div className="d-flex ms-3 gap-3">
                      {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map(key => (
                        <Flex
                          key={key}
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <h6 className="mb-0 fw-bold text-700 ">
                            {key === 'all' ? 'همه' : key.toUpperCase()}
                          </h6>
                          <p className={`mb-0 fs-0 text-900 fw-medium`}>
                            <TextLoading height={10} isWidthPx width={15} />
                          </p>
                        </Flex>
                      ))}
                    </div>
                    <div className="d-flex gap-3 ms-5 justify-content-start">
                      <Flex
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <h6 className="mb-0 fw-bold text-700 ">تصاویر</h6>
                        <p className={`mb-0 fs-0 text-900 fw-medium`}>
                          <TextLoading height={10} isWidthPx width={15} />
                        </p>
                      </Flex>
                      <Flex
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <h6 className="mb-0 fw-bold text-700 ">پیوندها</h6>
                        <p className={`mb-0 fs-0 text-900 fw-medium`}>
                          <TextLoading height={10} isWidthPx width={15} />
                        </p>
                      </Flex>
                    </div>
                  </Col>
                  <Col
                    xs={12}
                    lg={6}
                    className="d-flex align-items-center justify-content-end"
                  >
                    <div className="d-flex fw-bold gap-3 ms-5 justify-content-start align-items-center">
                      <span>Sitemap.xml</span>
                      <span> Robots.txt</span>
                    </div>
                  </Col>
                </Row>{' '}
              </Card.Body>
            </Card>
            <div className="my-3">
              <AgGridSkeleton height="600px" />
            </div>
            <div className="my-3">
              <AgGridSkeleton height="600px" />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default InternalSeo;
